import { execFileSync, execSync } from 'node:child_process';
import { existsSync, realpathSync } from 'node:fs';
import chalk from 'chalk';
import ora from 'ora';
import { getHost } from '../lib/config.js';
import { success, error, info, warn } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

declare const __CLI_VERSION__: string;

const cliVersion = typeof __CLI_VERSION__ !== 'undefined' ? __CLI_VERSION__ : 'dev';

export interface VersionInfo {
  latest: string;
  download_url: string;
  changelog_url?: string;
}

/**
 * Fetch the latest CLI version from the API.
 * Returns null if the API is unreachable (silent fail).
 */
export async function fetchLatestVersion(): Promise<VersionInfo | null> {
  const host = getHost();
  if (!host) return null;

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5_000);
    timeout.unref();

    const res = await fetch(`${host}/cli/version`, {
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (!res.ok) return null;

    return (await res.json()) as VersionInfo;
  } catch {
    // Network error, timeout, etc. — silently fail
    return null;
  }
}

/**
 * Compare two semver strings. Returns true if remote > local.
 */
function isNewerVersion(local: string, remote: string): boolean {
  if (local === 'dev') return false;

  const parse = (v: string) => v.replace(/^v/, '').split('.').map(Number);
  const lParts = parse(local);
  const rParts = parse(remote);
  const lMaj = lParts[0] ?? 0, lMin = lParts[1] ?? 0, lPat = lParts[2] ?? 0;
  const rMaj = rParts[0] ?? 0, rMin = rParts[1] ?? 0, rPat = rParts[2] ?? 0;

  if (rMaj !== lMaj) return rMaj > lMaj;
  if (rMin !== lMin) return rMin > lMin;
  return rPat > lPat;
}

/**
 * Detect running manager process and active agent tmux sessions.
 * Returns a summary of what's running.
 *
 * ENG-4635: pgrep can race with process exit — by the time we report
 * "Manager running PID X", X may already be a tombstone. The agt-update
 * report-then-exit pattern made this acutely visible (operators saw
 * warnings about phantom PIDs that ps -p couldn't find). Verify each
 * candidate PID with kill(pid, 0) before reporting it. kill(pid, 0)
 * doesn't actually signal — it just probes whether the OS still
 * recognises the PID and whether we have permission to signal it.
 * ESRCH means dead/recycled; we treat that as "not running". EPERM
 * means alive but owned by someone else — still alive, count it.
 */
// Exported for unit testing — see __tests__/update-pid-check.test.ts.
export function isPidAlive(pid: number): boolean {
  if (!Number.isInteger(pid) || pid <= 0) return false;
  try {
    process.kill(pid, 0);
    return true;
  } catch (err) {
    // EPERM = process exists but owned by a different uid; still alive.
    // ESRCH = no such process. Anything else is unexpected — treat as
    // "unknown" and assume not alive so we don't block updates on a
    // weird PID lookup error.
    return (err as NodeJS.ErrnoException).code === 'EPERM';
  }
}

function detectActiveSessions(): { managerPid: string | null; tmuxSessions: string[] } {
  let managerPid: string | null = null;
  try {
    const pid = execSync('pgrep -f "agt.*(manage|manager)" 2>/dev/null || true', { encoding: 'utf-8' }).trim();
    // Filter out our own PID and verify the survivors are actually alive.
    // pgrep can win the race with a fleeting process (e.g. another
    // `agt manager status` from a concurrent script) and report a PID
    // that has already exited — without the kill(0) check we'd warn
    // operators about a phantom manager and block their update.
    const ownPid = String(process.pid);
    const pids = pid
      .split('\n')
      .map((p) => p.trim())
      .filter((p) => p && p !== ownPid)
      .filter((p) => isPidAlive(Number(p)));
    managerPid = pids[0] ?? null;
  } catch { /* no manager running */ }

  const tmuxSessions: string[] = [];
  try {
    const sessions = execSync('tmux ls 2>/dev/null || true', { encoding: 'utf-8' }).trim();
    if (sessions) {
      for (const line of sessions.split('\n')) {
        // Agent sessions use patterns like "claude-{codeName}" or "agt-{codeName}"
        if (line.match(/^(claude-|agt-)/)) {
          const name = line.split(':')[0];
          if (name) tmuxSessions.push(name);
        }
      }
    }
  } catch { /* tmux not available or no sessions */ }

  return { managerPid, tmuxSessions };
}

/**
 * Detect how the currently running `agt` binary was installed.
 *
 * Returns 'brew' only when the resolved path is inside a Homebrew Cellar
 * (the canonical formula install location: `.../Cellar/<formula>/<version>/bin/…`).
 * A bare `/home/linuxbrew/` or `/opt/homebrew/` prefix isn't enough —
 * Homebrew's Node hosts `npm install -g` targets like
 * `/opt/homebrew/lib/node_modules/@integrity-labs/agt-cli/...`, which is
 * an npm install *managed by* npm, not a brew formula.
 */
function detectInstallSource(): 'brew' | 'npm' {
  try {
    const resolved = realpathSync(process.argv[1] ?? '');
    // Match /Cellar/<formula>/ specifically — works for both
    // /home/linuxbrew/.linuxbrew/Cellar and /opt/homebrew/Cellar.
    if (/\/Cellar\/[^/]+\//.test(resolved)) {
      return 'brew';
    }
  } catch { /* fall through */ }
  return 'npm';
}

/**
 * Perform the actual update. Uses brew if the CLI was installed that way,
 * otherwise npm from the public registry (where we actually publish —
 * the earlier implementation pointed at GitHub Packages, which is wrong).
 */
function performUpdate(version: string): void {
  const source = detectInstallSource();
  if (source === 'brew') {
    try {
      execFileSync('brew', ['upgrade', 'integrity-labs/tap/agt'], { stdio: 'inherit' });
      return;
    } catch (err) {
      // Only attempt the sudo -u <owner> retry when running as root —
      // otherwise a genuine failure (network, formula syntax error,
      // missing dependency) gets masked by a second auth error.
      const isRoot = typeof process.getuid === 'function' && process.getuid() === 0;
      if (!isRoot) throw err;
      const cellarOwner = detectBrewOwner();
      if (!cellarOwner) throw err;
      execFileSync('sudo', [
        '-u', cellarOwner,
        '-H',
        'bash', '-c',
        'brew tap integrity-labs/tap 2>/dev/null || true; brew upgrade integrity-labs/tap/agt',
      ], { stdio: 'inherit' });
    }
    return;
  }
  // npm path — public registry (this is where publishConfig points).
  // Use execFileSync with an argv array so the version string (which
  // originates from a remote API response) can't be interpreted by a
  // shell. Any attacker controlling the API can still push a malicious
  // package, but they can't also inject shell metacharacters via the
  // version field on top of that.
  execFileSync('npm', [
    'install',
    '-g',
    `@integrity-labs/agt-cli@${version}`,
    '--registry=https://registry.npmjs.org',
  ], { stdio: 'inherit' });
}

function detectBrewOwner(): string | null {
  for (const prefix of ['/home/linuxbrew/.linuxbrew', '/opt/homebrew', '/usr/local']) {
    const cellar = `${prefix}/Cellar`;
    if (!existsSync(cellar)) continue;
    try {
      return execSync(`stat -c %U "${cellar}" 2>/dev/null || stat -f %Su "${cellar}"`, { encoding: 'utf-8' }).trim() || null;
    } catch { /* try next */ }
  }
  return null;
}

/**
 * `agt update` command handler.
 */
export async function updateCommand(opts: { force?: boolean } = {}): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Checking for updates…', isSilent: json });
  spinner.start();

  const versionInfo = await fetchLatestVersion();

  if (!versionInfo) {
    spinner.stop();
    if (json) {
      jsonOutput({ ok: false, error: 'Could not reach API to check for updates' });
    } else {
      warn('Could not reach the API to check for updates. Check AGT_HOST is set and the API is reachable.');
    }
    process.exitCode = 1;
    return;
  }

  const updateAvailable = isNewerVersion(cliVersion, versionInfo.latest);

  if (!updateAvailable) {
    spinner.stop();
    if (json) {
      jsonOutput({ ok: true, current: cliVersion, latest: versionInfo.latest, update_available: false });
    } else {
      success(`You're on the latest version (${chalk.bold(cliVersion)})`);
    }
    return;
  }

  spinner.stop();

  if (!json) {
    info(`Update available: ${chalk.dim(cliVersion)} → ${chalk.bold.green(versionInfo.latest)}`);
    if (versionInfo.changelog_url) {
      info(`Changelog: ${versionInfo.changelog_url}`);
    }
  }

  // Check for active sessions unless --force is set
  if (!opts.force) {
    const { managerPid, tmuxSessions } = detectActiveSessions();

    if (managerPid || tmuxSessions.length > 0) {
      if (!json) {
        warn('Active processes detected:');
        if (managerPid) {
          console.error(chalk.yellow(`  • Manager process running (PID ${managerPid})`));
        }
        if (tmuxSessions.length > 0) {
          console.error(chalk.yellow(`  • ${tmuxSessions.length} active agent session(s): ${tmuxSessions.join(', ')}`));
        }
        console.error();
        warn('Updating while the manager is running will leave it on the old version until restarted.');
        warn('Active agent sessions will continue with stale MCP servers.');
        console.error();
        info(`Run ${chalk.bold('agt update --force')} to update anyway, then restart the manager.`);
      } else {
        jsonOutput({
          ok: false,
          current: cliVersion,
          latest: versionInfo.latest,
          update_available: true,
          updated: false,
          blocked: true,
          manager_running: !!managerPid,
          active_sessions: tmuxSessions,
          error: 'Active manager or agent sessions detected. Use --force to override.',
        });
      }
      process.exitCode = 1;
      return;
    }
  }

  const updateSpinner = ora({ text: 'Installing update…', isSilent: json });
  updateSpinner.start();

  try {
    performUpdate(versionInfo.latest);
    updateSpinner.stop();
    if (json) {
      jsonOutput({
        ok: true,
        current: cliVersion,
        latest: versionInfo.latest,
        update_available: true,
        updated: true,
      });
    } else {
      success(`Updated to ${chalk.bold(versionInfo.latest)}`);
      info(`If you have a running manager, restart it: ${chalk.bold('agt manager stop && agt manager start')}`);
    }
  } catch (err) {
    updateSpinner.fail('Update failed');
    if (json) {
      jsonOutput({
        ok: false,
        current: cliVersion,
        latest: versionInfo.latest,
        update_available: true,
        updated: false,
        error: (err as Error).message,
      });
    } else {
      error(`Failed to install update: ${(err as Error).message}`);
      const source = detectInstallSource();
      if (source === 'brew') {
        info(`You can manually update with: brew upgrade integrity-labs/tap/agt`);
      } else {
        info(`You can manually update with: sudo npm install -g @integrity-labs/agt-cli@${versionInfo.latest}`);
      }
    }
    process.exitCode = 1;
  }
}

/**
 * Non-blocking startup version check. Prints a one-liner if an update is available.
 * Silently does nothing on failure.
 */
export async function checkForUpdateOnStartup(): Promise<void> {
  try {
    const versionInfo = await fetchLatestVersion();
    if (!versionInfo) return;

    if (isNewerVersion(cliVersion, versionInfo.latest)) {
      console.error(
        chalk.yellow(`\n  Update available: ${cliVersion} → ${versionInfo.latest}. Run ${chalk.bold('agt update')} to install.`)
        + chalk.dim('\n  Note: Restart the manager after updating.\n')
      );
    }
  } catch {
    // Silent fail — never block CLI usage
  }
}
